import React from 'react';
import { cn } from '../../lib/utils';
export interface AvatarStackMember {
  avatar?: string;
  name: string;
  /** Optional slug — enables click-through to the member's profile. */
  slug?: string;
}
export interface AvatarStackProps {
  /** All members of the group (the listing creator is always the first member). */
  members: AvatarStackMember[];
  /** Max real avatars to render. Extra members are silently dropped — the
   * accompanying "Grup de N" label carries the true count. */
  maxVisible?: number;
  size?: 'md' | 'lg';
  className?: string;
  /** Optional click handler — when set, the stack becomes a button. */
  onClick?: (e: React.MouseEvent) => void;
  /** Accessible label for the whole stack (overrides default). */
  ariaLabel?: string;
}
// Primary avatar is slightly larger than secondaries to give the listing
// creator subtle visual weight (industry pattern — Spotify Blend, etc).
// Overlap is tight so 3 avatars sit roughly in the footprint of one solo
// avatar, keeping the header compact.
const SIZE_MAP = {
  md: {
    primary: 'w-12 h-12',
    secondary: 'w-10 h-10',
    overlap: '-ml-7',
    text: 'text-sm',
    ring: 'ring-2'
  },
  lg: {
    primary: 'w-14 h-14',
    secondary: 'w-11 h-11',
    overlap: '-ml-8',
    text: 'text-base',
    ring: 'ring-2'
  }
};
function initials(name: string) {
  return name.trim().charAt(0).toUpperCase() || '?';
}
/**
 * AvatarStack — visual identifier for a finalized combo-rent group.
 *
 * Rules (MVP, invite-only groups):
 * - Up to `maxVisible` avatars render (default 3). Extra members are
 *   silently dropped — the accompanying "Grup de N" label carries the
 *   true count. No "+N" trailing text (keeps the header structure clean).
 * - The first avatar (the listing creator) is rendered slightly larger
 *   than the rest, giving a quiet visual hierarchy.
 * - Secondaries are heavily tucked under the primary so 3 avatars stay
 *   roughly within the footprint of one solo avatar.
 * - When `onClick` is provided, the whole stack becomes a button.
 */
export function AvatarStack({
  members,
  maxVisible = 3,
  size = 'md',
  className,
  onClick,
  ariaLabel
}: AvatarStackProps) {
  const sizes = SIZE_MAP[size];
  const total = members.length;
  const visible = members.slice(0, maxVisible);
  const computedAriaLabel = ariaLabel || `Grup · ${total} ${total === 1 ? 'persoană' : 'persoane'}`;
  const Wrapper: any = onClick ? 'button' : 'div';
  const wrapperProps = onClick ? {
    type: 'button' as const,
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick(e);
    },
    'aria-label': computedAriaLabel,
    className: cn('relative inline-flex items-center outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-full cursor-pointer', className)
  } : {
    role: 'img' as const,
    'aria-label': computedAriaLabel,
    className: cn('relative inline-flex items-center', className)
  };
  return <Wrapper {...wrapperProps} data-id="element-2799">
      <div className="flex items-center" data-id="element-2800">
        {visible.map((m, idx) => {
        const isPrimary = idx === 0;
        return <div key={`${m.name}-${idx}`} className={cn('rounded-full overflow-hidden bg-dark-3 shrink-0 ring-dark-1', isPrimary ? sizes.primary : sizes.secondary, sizes.ring, !isPrimary && sizes.overlap)} style={{
          zIndex: visible.length - idx
        }} data-id="element-2801">
              {m.avatar ? <img src={m.avatar} alt={m.name} className="w-full h-full object-cover" data-id="element-2802" /> : <div className={cn('w-full h-full flex items-center justify-center text-light-2 font-medium', sizes.text)} data-id="element-2803">
                  {initials(m.name)}
                </div>}
            </div>;
      })}
      </div>
    </Wrapper>;
}